import axios from 'axios';
import { storage } from '@byscott-io/corebyscott';

const client = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token and organization header to requests
client.interceptors.request.use((config) => {
  const token = storage.getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const containerId = storage.getContainerId();
  if (containerId) {
    config.headers['X-Organization-Id'] = containerId;
  }
  return config;
});

// Handle auth errors
client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      storage.clear();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Dashboard
export const dashboard = {
  get: () => client.get('/dashboard'),
};

// Recipes
export const recipes = {
  list: (params) => client.get('/recipes', { params }),
  get: (id) => client.get(`/recipes/${id}`),
  create: (data) => client.post('/recipes', data),
  update: (id, data) => client.patch(`/recipes/${id}`, data),
  delete: (id) => client.delete(`/recipes/${id}`),
  search: (params) => client.get('/recipes/search', { params }),
  history: (id) => client.get(`/recipes/${id}/history`),
  feelingLucky: (params) => client.get('/recipes/feeling_lucky', { params }),
  importFromUrl: (url) => client.post('/recipes/import_from_url', { url }),
  scanCard: (imageFile) => {
    const formData = new FormData();
    formData.append('image', imageFile);
    return client.post('/recipes/scan_card', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  favorite: (id) => client.post(`/recipes/${id}/favorite`),
  unfavorite: (id) => client.delete(`/recipes/${id}/unfavorite`),
};

// Ingredients
export const ingredients = {
  list: (recipeId) => client.get(`/recipes/${recipeId}/ingredients`),
  create: (recipeId, data) => client.post(`/recipes/${recipeId}/ingredients`, data),
  update: (recipeId, id, data) => client.patch(`/recipes/${recipeId}/ingredients/${id}`, data),
  delete: (recipeId, id) => client.delete(`/recipes/${recipeId}/ingredients/${id}`),
};

// Recipe Categories
export const recipeCategories = {
  list: () => client.get('/recipe_categories'),
  create: (data) => client.post('/recipe_categories', data),
  update: (id, data) => client.patch(`/recipe_categories/${id}`, data),
  delete: (id) => client.delete(`/recipe_categories/${id}`),
};

// Shopping Lists
export const shoppingLists = {
  list: (params) => client.get('/shopping_lists', { params }),
  get: (id) => client.get(`/shopping_lists/${id}`),
  create: (data) => client.post('/shopping_lists', data),
  update: (id, data) => client.patch(`/shopping_lists/${id}`, data),
  delete: (id) => client.delete(`/shopping_lists/${id}`),
  generateFromRecipes: (id) => client.post(`/shopping_lists/${id}/generate_from_recipes`),
};

// Shopping List Items
export const shoppingListItems = {
  list: (listId) => client.get(`/shopping_lists/${listId}/shopping_list_items`),
  create: (listId, data) => client.post(`/shopping_lists/${listId}/shopping_list_items`, data),
  update: (listId, id, data) => client.patch(`/shopping_lists/${listId}/shopping_list_items/${id}`, data),
  delete: (listId, id) => client.delete(`/shopping_lists/${listId}/shopping_list_items/${id}`),
  bulkCheck: (listId, itemIds, checked) =>
    client.post(`/shopping_lists/${listId}/shopping_list_items/bulk_check`, { item_ids: itemIds, checked }),
};

// Shopping List Recipes
export const shoppingListRecipes = {
  add: (listId, recipeId, multiplier = 1) =>
    client.post(`/shopping_lists/${listId}/shopping_list_recipes`, { recipe_id: recipeId, servings_multiplier: multiplier }),
  remove: (listId, id) => client.delete(`/shopping_lists/${listId}/shopping_list_recipes/${id}`),
};

// Meals
export const meals = {
  list: (params) => client.get('/meals', { params }),
  get: (id) => client.get(`/meals/${id}`),
  create: (data) => client.post('/meals', data),
  update: (id, data) => client.patch(`/meals/${id}`, data),
  delete: (id) => client.delete(`/meals/${id}`),
  calendar: (startDate, endDate) => client.get('/meal_calendar', { params: { start_date: startDate, end_date: endDate } }),
};

// Meal Recipes
export const mealRecipes = {
  add: (mealId, recipeId, servingsOverride) =>
    client.post(`/meals/${mealId}/meal_recipes`, { recipe_id: recipeId, servings_override: servingsOverride }),
  remove: (mealId, id) => client.delete(`/meals/${mealId}/meal_recipes/${id}`),
};

// Public Recipes (no auth)
export const publicRecipes = {
  list: (params) => axios.get('/api/public/recipes', { params }),
  get: (id) => axios.get(`/api/public/recipes/${id}`),
  search: (params) => axios.get('/api/public/recipes/search', { params }),
  feelingLucky: (params) => axios.get('/api/public/recipes/feeling_lucky', { params }),
  categories: () => axios.get('/api/public/recipes/categories'),
  // Anonymous submission of a new recipe for maintainer review (#520).
  // Submit payload bot_check fields come from <BotCheckWidget>.
  submit: (payload) => axios.post('/api/public/recipes', payload),
  botCheckChallengeUrl: '/api/public/recipes/bot_check_challenge',
};

// Maintainer review queue for anonymous public submissions (#520)
export const pendingRecipes = {
  list: () => client.get('/admin/pending_recipes'),
  approve: (id) => client.post(`/admin/pending_recipes/${id}/approve`),
  reject: (id) => client.delete(`/admin/pending_recipes/${id}`),
};

// Dietary protocols (catalog of known eating protocols, global + user-private)
export const dietaryProtocols = {
  list: (params) => client.get('/dietary_protocols', { params }),
  get: (id) => client.get(`/dietary_protocols/${id}`),
};

// Family (kitchen-wide) dietary profile — chosen in admin UI
export const familyDietaryProfile = {
  get: () => client.get('/family_dietary_profile'),
  update: (data) => client.patch('/family_dietary_profile', { family_dietary_profile: data }),
};

// Household members — the per-person roster within a kitchen.
// Each member may or may not be linked to a User; each carries its
// own dietary profile (protocol, hard_avoids, etc.).
export const householdMembers = {
  list: () => client.get('/household_members'),
  get: (id) => client.get(`/household_members/${id}`),
  create: (data) => client.post('/household_members', { household_member: data }),
  update: (id, data) => client.patch(`/household_members/${id}`, { household_member: data }),
  delete: (id) => client.delete(`/household_members/${id}`),
  nutrition: (id, { from, to } = {}) => client.get(`/household_members/${id}/nutrition`, { params: { from, to } }),
};

// Meal servings — record what each household member actually ate
// from a planned meal. Bulk upsert.
export const mealServings = {
  record: (mealId, servings) => client.post(`/meals/${mealId}/servings`, { servings }),
};

// Helper function for recipe card scanning
export const scanRecipeCard = async (imageFile) => {
  const response = await recipes.scanCard(imageFile);
  return response.data;
};

export default client;
